import { useState } from 'react'
import { DocumentArrowDownIcon, CalendarIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'

export default function ExportsPage() {
  const [loading, setLoading] = useState({})
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  })

  const handleExport = async (type, filename) => {
    try {
      setLoading(prev => ({ ...prev, [type]: true }))
      
      const params = {}
      if (dateRange.startDate) params.startDate = dateRange.startDate
      if (dateRange.endDate) params.endDate = dateRange.endDate
      
      const response = await api.get(`/export/${type}`, {
        params,
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
    } catch (error) {
      console.error(`Failed to export ${type}:`, error)
      alert('내보내기에 실패했습니다.')
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }))
    }
  }

  const exportOptions = [
    {
      id: 'users',
      title: '사용자 데이터',
      description: '모든 사용자 정보와 잔액 데이터를 내보냅니다.',
      filename: 'users_export'
    },
    {
      id: 'transactions',
      title: '거래내역',
      description: '모든 거래내역을 내보냅니다.',
      filename: 'transactions_export'
    },
    {
      id: 'withdrawals',
      title: '출금 데이터',
      description: '모든 출금 요청과 처리 상태를 내보냅니다.',
      filename: 'withdrawals_export'
    },
    {
      id: 'referral-codes',
      title: '담당자코드',
      description: '담당자코드와 사용 통계를 내보냅니다.',
      filename: 'referral_codes_export'
    },
    {
      id: 'staking-configs',
      title: '스테이킹 설정',
      description: '모든 스테이킹 설정과 보상 내역을 내보냅니다.',
      filename: 'staking_configs_export'
    },
    {
      id: 'system-data',
      title: '전체 시스템 데이터',
      description: '모든 데이터를 포함한 종합 리포트를 내보냅니다.',
      filename: 'system_data_export'
    }
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">데이터 내보내기</h1>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900">날짜 범위 설정</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              시작일
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              종료일
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <p className="text-sm text-gray-500 mt-2">
          날짜를 설정하지 않으면 전체 데이터를 내보냅니다.
        </p>
      </div>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {exportOptions.map((option) => (
          <div key={option.id} className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <DocumentArrowDownIcon className="h-8 w-8 text-blue-600" />
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {option.title}
            </h3>
            
            <p className="text-sm text-gray-500 mb-4">
              {option.description}
            </p>
            
            <button
              onClick={() => handleExport(option.id, option.filename)}
              disabled={loading[option.id]}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading[option.id] ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  내보내는 중...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4" />
                  Excel로 내보내기
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">사용 안내</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 내보낸 파일은 Excel 형식(.xlsx)으로 다운로드됩니다.</li>
          <li>• 대용량 데이터의 경우 내보내기에 시간이 걸릴 수 있습니다.</li>
          <li>• 날짜 범위를 설정하여 특정 기간의 데이터만 내보낼 수 있습니다.</li>
          <li>• 전체 시스템 데이터는 모든 테이블의 데이터를 포함합니다.</li>
        </ul>
      </div>
    </div>
  )
}